import React, { useRef, useEffect, useState } from "react";
import microscope from "../assets/microscope.jpeg";
import "../styles/WhyChoose.css";

/* ── Patients data ── */
const patients = [
  {
    name: "Ramesh Kumar",
    location: "Chennai",
    review:
      "Excellent lab with accurate reports and very quick service. Highly recommended!",
    rating: 5,
    avatar: "https://randomuser.me/api/portraits/men/52.jpg",
  },
  {
    name: "Priya Lakshmi",
    location: "Coimbatore",
    review:
      "Very professional staff and hygienic collection process. Reports were delivered on time.",
    rating: 5,
    avatar: "https://randomuser.me/api/portraits/women/70.jpg",
  },
  {
    name: "Suresh Babu",
    location: "Tirupur",
    review:
      "Affordable pricing with no compromise on quality. The home collection service is amazing.",
    rating: 5,
    avatar: "https://randomuser.me/api/portraits/men/70.jpg",
  },
];

const reasons = [
  "NABL Quality Standards",
  "Advanced Technology",
  "Experienced Pathologists",
  "Affordable Pricing",
  "Quick & Accurate Reports",
  "Patient-Centric Approach",
];

/* ── Star renderer ── */
const Stars = ({ count }) => (
  <div className="wc-stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <i key={i} className={`fa-star ${i < count ? "fa-solid" : "fa-regular"}`}></i>
    ))}
  </div>
);

/* ── Main Component ── */
export default function WhyChoose() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);
  const [slideState, setSlideState] = useState("idle");
  const [direction, setDirection] = useState("next");
  const timerRef = useRef(null);

  /* IntersectionObserver */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  /* Timer helper */
  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => triggerSlide("next"), 5000);
  };

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Slide out → swap → slide in */
  const triggerSlide = (dir) => {
    setDirection(dir);
    setSlideState(dir === "next" ? "out-left" : "out-right");
    setTimeout(() => {
      setActive((prev) =>
        dir === "next"
          ? (prev + 1) % patients.length
          : (prev - 1 + patients.length) % patients.length
      );
      setSlideState("in");
      setTimeout(() => setSlideState("idle"), 420);
    }, 360);
  };

  const goTo = (idx) => {
    if (idx === active) return;
    triggerSlide(idx > active ? "next" : "prev");
    startTimer();
  };

  const current = patients[active];

  const panelClass =
    slideState === "out-left"  ? "wc-panel-out-left"  :
    slideState === "out-right" ? "wc-panel-out-right" :
    slideState === "in"
      ? direction === "next" ? "wc-panel-in-right" : "wc-panel-in-left"
      : "";

  return (
    <section className="wc-section" ref={sectionRef}>
      <div className="container-fluid px-4">
        <div className="row g-4 align-items-stretch">

          {/* ════════════════════════
              LEFT CARD — Why Choose
          ════════════════════════ */}
          <div className="col-12 col-lg-6">
            <div className={`wc-card wc-card-why wc-anim-left ${visible ? "wc-anim-in" : ""}`}>
              <div className="wc-img-layer">
                <img src={microscope} alt="Microscope" loading="lazy" className="wc-microscope-img" />
              </div>
              <div className="wc-fade-overlay"></div>
              <div className="wc-why-content">
                <span className="wc-eyebrow">WHY CHOOSE TANBIO?</span>
                <ul className="wc-reasons-list list-unstyled">
                  {reasons.map((r, i) => (
                    <li
                      key={i}
                      className={`wc-reason-item wc-anim-up ${visible ? "wc-anim-in" : ""}`}
                      style={{ transitionDelay: `${0.25 + i * 0.08}s` }}
                    >
                      <span className="wc-check-icon">
                        <i className="fa-solid fa-circle-check"></i>
                      </span>
                      <span className="wc-reason-text">{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* ════════════════════════
              RIGHT CARD — Testimonial
              Layout: avatar LEFT | text content RIGHT
          ════════════════════════ */}
          <div className="col-12 col-lg-6">
            <div className={`wc-card wc-card-testimonial wc-anim-right ${visible ? "wc-anim-in" : ""}`}>

              {/* Avatar col — LEFT side, vertically centered */}
              <div className="wc-avatar-col">
                <div className="wc-avatar-circle" key={`avatar-${active}`}>
                  <img
                    src={current.avatar}
                    alt={current.name}
                    loading="lazy"
                    className="wc-avatar-img"
                  />
                </div>
              </div>

              {/* Text content — RIGHT side */}
              <div className="wc-testimonial-content">
                <span className="wc-eyebrow">WHAT OUR PATIENTS SAY</span>

                {/* Slide viewport */}
                <div className="wc-slide-viewport">
                  <div className={`wc-slide-panel ${panelClass}`} key={active}>
                    <div className="wc-quote-icon">
                      <i className="fa-solid fa-quote-left"></i>
                    </div>
                    <p className="wc-review-text">{current.review}</p>
                    <Stars count={current.rating} />
                    <p className="wc-patient-name">— {current.name}</p>
                    <p className="wc-patient-location">
                      <i className="fa-solid fa-location-dot me-1"></i>
                      {current.location}
                    </p>
                  </div>
                </div>

                {/* Dots */}
                <div className="wc-dots">
                  {patients.map((_, i) => (
                    <button
                      key={i}
                      className={`wc-dot ${i === active ? "wc-dot-active" : ""}`}
                      onClick={() => goTo(i)}
                      aria-label={`Review ${i + 1}`}
                    />
                  ))}
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}